/**
 * @file ItemSourceIcon.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-21
 * @modified 2026-09-21
 * @description Where an inventory item came from, as one mark beside its status
 *              badge.
 *
 *              Three states, and the quiet one is the common one: an item that
 *              came from Xero shows NOTHING. Over four thousand of the
 *              catalogue is Xero's, and a mark on every row would be a column
 *              of identical icons saying "normal" — which is a decoration, not
 *              information. The mark appears only where the row is unusual.
 *
 *              The two that do show say different things and are weighted
 *              accordingly. `cloud_done` is barely there: made here, safely in
 *              Xero, nothing to do — a reminder, not a flag. `cloud_off` is in
 *              the error colour because it is a real problem: the item exists
 *              in this system and nowhere else, so nothing anyone does with it
 *              will reach the ledger.
 *
 *              The status itself is decided in SQL (INV_SOURCE_SQL in
 *              routes/inventory.js) and arrives as `source_status`, so the
 *              filter and this mark cannot disagree about what a row is.
 */

import React from 'react';
import './ItemSourceIcon.css';

/** The three, with the wording used by the filter and the printed export. */
export const ITEM_SOURCES = {
  xero: {
    label: 'Xero',
    icon: null,
    title: 'Came from Xero'
  },
  local: {
    label: 'Local',
    icon: 'cloud_done',
    title: 'Created in this system, and pushed to Xero'
  },
  local_only: {
    label: 'Local only',
    icon: 'cloud_off',
    title: 'Created in this system and never pushed to Xero'
  }
};

/**
 * The status, from the server where it can be and from the item's own Xero id
 * where it cannot.
 *
 * The fallback covers a caller whose endpoint does not send `source_status`
 * yet. It can spot `local_only` — no real Xero id is a fact the row carries —
 * but never `local`, because a pushed local item is indistinguishable from a
 * Xero one without the column. Erring towards the quiet state is the right way
 * round: a missing reminder is nothing, a false alarm is noise.
 */
export const itemSourceOf = (item) => {
  if (!item) return null;
  if (item.source_status) return item.source_status;
  const id = item.xero_item_id;
  if (!id || String(id).startsWith('LOCAL-') || String(id).startsWith('TEMP-')) return 'local_only';
  return 'xero';
};

function ItemSourceIcon({ item, source = null }) {
  const key = source || itemSourceOf(item);
  const spec = ITEM_SOURCES[key];
  if (!spec || !spec.icon) return null;

  return (
    <span
      className={`material-symbols-rounded item-source-icon is-${key}`}
      title={spec.title}
      aria-label={spec.title}
    >
      {spec.icon}
    </span>
  );
}

export default ItemSourceIcon;
